import React from 'react';

import { Tooltip, Typography } from '@mui/material';

import { ICondition } from '../interfaces/IRuleResults';

const OPERATORS = {
  EQUAL: 'eq',
  GREATER: 'gt',
  LESS: 'lt',
  GREATER_EQ: 'ge',
  LESS_EQ: 'le',
  IN: 'in',
  BETWEEN: 'bt',
  HAS: 'has',
  NOT_EQUAL: 'ne',
  NOT_BETWEEN: 'not_bt',
  NOT_IN: 'not_in',
  NOT_HAS: 'not_has',
};

const DECISION_GRADE_MAP: { [key: string]: string } = {
  '1': 'preferred_plus',
  '2': 'preferred',
  '4': 'standard_plus',
  '5': 'standard',
  '6': 'standard 25',
  '7': 'standard 50',
  '8': 'standard 75',
  '9': 'standard 100',
  '10': 'standard 125',
  '11': 'standard 150',
  '12': 'standard 175',
  '13': 'standard 200',
  '14': 'standard 225',
  '15': 'standard 250',
};

const renderPlus = (key: string) => (
  <span key={key} style={{ margin: '0 0.8em 0 0.8em' }}>
    +
  </span>
);
const openSquare = <span style={{ margin: '0 0.5em 0 0.8em' }}>[</span>;
const closeSquare = <span style={{ margin: '0 0 0 0.5em' }}>]</span>;
const arrow = <span style={{ margin: '0 0.8em 0 0.8em' }}>→</span>;
const phrase = (text: string, isUnderline: boolean = false) => (
  <span
    style={{
      borderBottom: isUnderline ? '1px dotted #7F858F' : 'none',
      cursor: 'default',
    }}
  >
    {text}
  </span>
);

// regex to turn map[key:true] into key
const extractMapKey = (value: string): string => {
  if (value.startsWith('map[')) {
    // Remove 'map[' from start and ']' from end
    const mapContent = value.slice(4, -1);

    // Split by space to handle multiple key-value pairs
    return (
      mapContent
        .split(' ')
        // For each pair, remove ':true' and replace underscores with spaces
        .map(pair => pair.replace(':true', '').replace(/_/g, ' '))
        .join(', ')
    );
  }
  return value;
};

const renderConditionAttributes = (condition: ICondition) => {
  if (condition.attributes?.length > 0) {
    return (
      <>
        {openSquare}
        {condition.attributes.reduce(
          (acc, attribute, attributeIdx, attributes) => {
            const skipTrueValue =
              attribute.operator === OPERATORS.EQUAL &&
              attribute.values?.length === 1 &&
              attribute.values[0] === 'true';

            const processValues = (values: string[]) => {
              if (attribute.name === 'decision_grade') {
                return values.map(value => DECISION_GRADE_MAP[value] || value);
              }
              return values.map(value => extractMapKey(value));
            };

            const processedValues = processValues(attribute.values || []);

            if (attribute.name === 'decision_grade') {
              acc.push(
                <span key={attributeIdx.toString()}>
                  {skipTrueValue ? null : phrase(processedValues.join(' , '))}
                </span>,
              );
            } else {
              acc.push(
                <span key={attributeIdx.toString()}>
                  {attribute.name && phrase(attribute.name)}
                  {!attribute.name || skipTrueValue ? null : ' : '}
                  {skipTrueValue ? null : phrase(processedValues.join(' , '))}
                </span>,
              );
            }

            if (attributeIdx !== attributes.length - 1) {
              acc.push(renderPlus(`${attributeIdx}plus`));
            }

            return acc;
          },
          [] as JSX.Element[],
        )}
        {closeSquare}
      </>
    );
  }

  return <></>;
};

const renderParentFact = (condition: ICondition) => {
  if (condition.parentFact) {
    return (
      <>
        <span>{phrase(condition.parentFact.factName)}</span>
        {arrow}
      </>
    );
  }

  return <></>;
};

const renderConditionValues = (condition: ICondition) => {
  if (condition.values?.length > 0) {
    const render = (values: any, isUnderline: boolean = false) => {
      return <span>: {phrase((values || []).join(' , '), isUnderline)}</span>;
    };

    if (
      [
        OPERATORS.BETWEEN,
        OPERATORS.IN,
        OPERATORS.LESS,
        OPERATORS.LESS_EQ,
        OPERATORS.GREATER,
        OPERATORS.GREATER_EQ,
        OPERATORS.HAS,
        OPERATORS.NOT_EQUAL,
        OPERATORS.NOT_BETWEEN,
        OPERATORS.NOT_IN,
        OPERATORS.NOT_HAS,
      ].includes(condition.operator)
    ) {
      let tooltipMsg = '';
      if (OPERATORS.BETWEEN === condition.operator) {
        const range = condition.rhValue
          .replace('[', '')
          .replace(']', '')
          .replace(' ', ' - ');
        tooltipMsg = `Between ${range}`;
      } else if (OPERATORS.NOT_BETWEEN === condition.operator) {
        const range = condition.rhValue
          .replace('[', '')
          .replace(']', '')
          .replace(' ', ' - ');
        tooltipMsg = `not between ${range}`;
      } else if (OPERATORS.IN === condition.operator) {
        const values = condition.rhValue.replace('[', '').replace(']', '');
        tooltipMsg = `One of: ${values}`;
      } else if (condition.operator === OPERATORS.LESS) {
        tooltipMsg = `Less than: ${condition.rhValue}`;
      } else if (condition.operator === OPERATORS.LESS_EQ) {
        tooltipMsg = `Less than or equal to: ${condition.rhValue}`;
      } else if (condition.operator === OPERATORS.GREATER) {
        tooltipMsg = `More than: ${condition.rhValue}`;
      } else if (condition.operator === OPERATORS.GREATER_EQ) {
        tooltipMsg = `More than or equal to: ${condition.rhValue}`;
      } else if (OPERATORS.HAS === condition.operator) {
        const values = condition.rhValue.replace('[', '').replace(']', '');
        tooltipMsg = `Contains one of: ${values}`;
      } else if (OPERATORS.NOT_HAS === condition.operator) {
        const values = condition.rhValue.replace('[', '').replace(']', '');
        tooltipMsg = `Contains none of: ${values}`;
      } else if (OPERATORS.NOT_EQUAL === condition.operator) {
        tooltipMsg = `Not equal to: ${condition.rhValue}`;
      }

      return (
        <Tooltip title={tooltipMsg} arrow>
          {render(condition.values, true)}
        </Tooltip>
      );
    }

    return render(condition.values);
  }

  return <></>;
};

const renderCondition = (condition: ICondition, key: string) => {
  // If operator is eq and the only value is true then we do not need
  // to render it as it indicates the presence of the fact.
  const skipTrueValue =
    condition.operator === OPERATORS.EQUAL &&
    condition.values?.length === 1 &&
    condition.values[0] === 'true';

  return (
    <span key={key}>
      {renderParentFact(condition)}
      {phrase(condition.factName)}
      {skipTrueValue ? null : renderConditionValues(condition)}
      {renderConditionAttributes(condition)}
    </span>
  );
};

export const Conditions = ({ data }: { data: ICondition[] }) => {
  return (
    <Typography variant="subtitle2" component="span">
      {data.reduce((acc, condition, conditionIdx, conditions) => {
        acc.push(renderCondition(condition, conditionIdx.toString()));

        if (conditionIdx !== conditions.length - 1) {
          acc.push(<br key={`${conditionIdx}break`} />);
        }

        return acc;
      }, [] as JSX.Element[])}
    </Typography>
  );
};
